/**
 * Facility Card Component - Enhanced UX/UI
 * Clean information display with address and actions
 * Improved visual hierarchy and interaction states
 */

import React from 'react';
import { Pencil, Copy, MapPin } from 'lucide-react';

/**
 * @typedef {Object} Facility
 * @property {string} id
 * @property {string} name
 * @property {string} address
 */

/**
 * @typedef {Object} FacilityCardProps
 * @property {Facility} facility
 * @property {boolean} isSelected
 * @property {(facilityId: string) => void} onSelect
 * @property {(facility: Facility) => void} [onEdit]
 * @property {(facility: Facility) => void} [onDuplicate]
 * @property {number} [locationsCount]
 */

export const FacilityCard = ({
  facility,
  isSelected,
  onSelect,
  onEdit,
  onDuplicate,
  locationsCount = 0, // kept for compatibility even if unused in UI yet
}) => {
  const handleCheckboxChange = () => {
    onSelect(facility.id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(facility);
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    onDuplicate?.(facility);
  };

  return (
    <div
      className={`
        bg-white rounded-[10px] px-5 py-4 w-full
        border-2 transition-all duration-200 cursor-pointer
        ${
          isSelected
            ? 'border-[#61b8e7] shadow-md shadow-[#61b8e7]/10'
            : 'border-[#e5e5e5] hover:border-[#61b8e7]/40 hover:shadow-sm'
        }
      `}
      onClick={handleCheckboxChange}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleCheckboxChange();
      }}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <div className="relative shrink-0 mt-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="peer size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all hover:border-[#61b8e7]"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Checkmark */}
          {isSelected && (
            <svg
              className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
              fill="none"
              viewBox="0 0 12 10"
            >
              <path
                d="M1.5 5L4.5 8L10.5 1.5"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name */}
          <h3
            className="font-['Roboto:Medium',sans-serif] font-medium text-[16px] text-[#1a1a1a] leading-tight mb-2"
            title={facility.name}
          >
            {facility.name}
          </h3>

          {/* Address */}
          <div className="flex items-start gap-2">
            <MapPin className="w-[14px] h-[14px] text-[#6e6e71] mt-0.5 shrink-0" />
            <p
              className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#6e6e71] leading-snug"
              title={
                facility.address ||
                (facility.street &&
                  `${facility.street}${facility.city ? `, ${facility.city}` : ''}${facility.state ? `, ${facility.state}` : ''}${facility.zipCode ? ` ${facility.zipCode}` : ''}`) ||
                'No address provided'
              }
            >
              {facility.address ||
                (facility.street &&
                  `${facility.street}${facility.city ? `, ${facility.city}` : ''}${facility.state ? `, ${facility.state}` : ''}${facility.zipCode ? ` ${facility.zipCode}` : ''}`) ||
                'No address provided'}
            </p>
          </div>
        </div>

        {/* Action Icons */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Edit Button */}
          {onEdit && (
            <button
              type="button"
              onClick={handleEdit}
              className="p-2 hover:bg-[#61b8e7]/10 rounded-md transition-colors group"
              aria-label="Edit facility"
              title="Edit facility"
            >
              <Pencil className="w-[16px] h-[16px] text-[#6e6e71] group-hover:text-[#61b8e7] transition-colors" />
            </button>
          )}

          {/* Duplicate Button */}
          {onDuplicate && (
            <button
              type="button"
              onClick={handleDuplicate}
              className="p-2 hover:bg-[#61b8e7]/10 rounded-md transition-colors group"
              aria-label="Duplicate facility"
              title="Duplicate facility"
            >
              <Copy className="w-[16px] h-[16px] text-[#6e6e71] group-hover:text-[#61b8e7] transition-colors" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
